import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, fmtError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field } from "@/components/AuthShell";
import { ShieldCheck, MapPin, Users, Bell, Camera, Mic, Navigation, Check, ArrowRight, ArrowLeft, Plus, Trash2, Loader2, PartyPopper, Upload } from "lucide-react";
import { toast } from "sonner";

const LANGS = ["English", "Hindi", "Bengali", "Marathi", "Tamil", "Telugu", "Odia", "Punjabi"];

export default function Onboarding() {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const [profile, setProfile] = useState({ full_name: "", phone: "", preferred_language: "English", date_of_birth: "", profile_photo_url: "" });
  const [community, setCommunity] = useState({ name: "", pin_code: "", city: "", state: "", address_landmark: "" });
  const [confirmed, setConfirmed] = useState(false);
  const [contacts, setContacts] = useState([{ name: "", relationship: "Family", phone: "", is_primary: true }]);

  const total = 6;

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload/profile-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile((p) => ({ ...p, profile_photo_url: data.url }));
      toast.success("Photo uploaded");
    } catch (err) { toast.error(fmtError(err.response?.data?.detail)); }
  };

  const searchCommunities = async (q) => {
    try {
      const { data } = await api.get(`/communities?search=${encodeURIComponent(q)}`);
      return data;
    } catch { return []; }
  };
  const [results, setResults] = useState([]);

  const next = async () => {
    if (step === 1) {
      if (!profile.full_name || !profile.phone) { toast.error("Full name and mobile number are required"); return; }
      setBusy(true);
      try { await api.put("/users/me", profile); } catch (e) { toast.error(fmtError(e.response?.data?.detail)); setBusy(false); return; }
      setBusy(false);
    }
    if (step === 2) {
      if (!community.name || !community.pin_code) { toast.error("Locality name and PIN code are required"); return; }
      if (!confirmed) { toast.error("Please confirm your home community"); return; }
      setBusy(true);
      try { await api.post("/users/me/home-community", community); } catch (e) { toast.error(fmtError(e.response?.data?.detail)); setBusy(false); return; }
      setBusy(false);
    }
    if (step === 3) {
      const valid = contacts.filter((c) => c.name && c.phone);
      setBusy(true);
      try { for (const c of valid) await api.post("/trusted-contacts", c); } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
      setBusy(false);
    }
    setStep((s) => Math.min(s + 1, total - 1));
  };

  const finish = async () => {
    setBusy(true);
    try {
      await api.post("/onboarding/complete");
      await refreshUser();
      navigate("/home", { replace: true });
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); setBusy(false); }
  };

  const shell = (content, footer) => (
    <div className="min-h-screen bg-[#FFF7E6] flex flex-col">
      <div className="max-w-md w-full mx-auto px-6 pt-8 pb-4 flex-1 flex flex-col">
        <div className="flex gap-1.5 mb-8">
          {Array.from({ length: total }).map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-[#C4B5FD]" : "bg-[#111827]/10"}`} />
          ))}
        </div>
        <motion.div key={step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="flex-1">
          {content}
        </motion.div>
        <div className="pt-6">{footer}</div>
      </div>
    </div>
  );

  const primaryBtn = (label, onClick, testId) => (
    <button onClick={onClick} disabled={busy} data-testid={testId} className="w-full bg-[#F97316] text-white rounded-2xl py-4 font-bold text-lg min-h-[56px] flex items-center justify-center gap-2 disabled:opacity-70 hover:-translate-y-0.5 transition-transform">
      {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{label} <ArrowRight className="w-5 h-5" /></>}
    </button>
  );

  const back = step > 0 && step < 5 && (
    <button onClick={() => setStep((s) => s - 1)} data-testid="onboarding-back" className="w-full mt-3 text-[#111827]/60 font-semibold py-2 flex items-center justify-center gap-1"><ArrowLeft className="w-4 h-4" /> Back</button>
  );

  if (step === 0)
    return shell(
      <div className="flex flex-col items-center text-center pt-10">
        <div className="w-20 h-20 rounded-3xl bg-[#F97316] flex items-center justify-center shadow-lg mb-6"><ShieldCheck className="w-10 h-10 text-white" /></div>
        <h1 className="text-4xl font-black font-head tracking-tight">Welcome to HumNest</h1>
        <p className="text-xl text-[#111827]/70 mt-4">Your neighbourhood response network.</p>
      </div>,
      primaryBtn("Let's get started", () => setStep(1), "onboarding-start")
    );

  if (step === 1)
    return shell(
      <div>
        <h1 className="text-3xl font-bold font-head tracking-tight">Your basic profile</h1>
        <p className="text-[#111827]/60 mt-1 mb-6">This helps neighbours recognise you.</p>
        <div className="flex items-center gap-4 mb-5">
          <div className="w-20 h-20 rounded-2xl bg-[#C4B5FD]/25 overflow-hidden flex items-center justify-center shrink-0">
            {profile.profile_photo_url ? <img src={fileUrl(profile.profile_photo_url)} alt="" className="w-full h-full object-cover" /> : <Users className="w-8 h-8 text-[#111827]/40" />}
          </div>
          <label data-testid="onboarding-photo-label" className="flex items-center gap-2 border-2 border-[#111827]/15 rounded-xl px-4 py-3 font-semibold cursor-pointer text-sm">
            <Upload className="w-4 h-4" /> Photo (optional)
            <input type="file" accept="image/*" className="hidden" data-testid="onboarding-photo" onChange={uploadPhoto} />
          </label>
        </div>
        <Field label="Full name" testId="onboarding-name" value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} placeholder="Anita Sharma" />
        <Field label="Mobile number" testId="onboarding-phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="+91 98350 12345" />
        <label className="block mb-4">
          <span className="text-sm font-bold uppercase tracking-wider text-[#111827]/70">Preferred language</span>
          <select data-testid="onboarding-language" value={profile.preferred_language} onChange={(e) => setProfile({ ...profile, preferred_language: e.target.value })} className="mt-1.5 bg-white border-2 border-[#111827]/15 rounded-xl p-4 text-lg w-full outline-none focus:border-[#F97316]">
            {LANGS.map((l) => <option key={l}>{l}</option>)}
          </select>
        </label>
        <Field label="Date of birth (optional)" testId="onboarding-dob" type="date" value={profile.date_of_birth} onChange={(e) => setProfile({ ...profile, date_of_birth: e.target.value })} />
      </div>,
      <>{primaryBtn("Continue", next, "onboarding-next-profile")}{back}</>
    );

  if (step === 2)
    return shell(
      <div>
        <div className="w-14 h-14 rounded-2xl bg-[#F97316]/10 flex items-center justify-center mb-4"><MapPin className="w-7 h-7 text-[#F97316]" /></div>
        <h1 className="text-3xl font-bold font-head tracking-tight">Your home community</h1>
        <p className="text-[#111827]/60 mt-1 mb-4">Where you live. Not assigned automatically from your location.</p>
        <input data-testid="community-search" placeholder="Search locality (e.g. Govindpur)" onChange={async (e) => setResults(await searchCommunities(e.target.value))} className="bg-white border-2 border-[#111827]/15 rounded-xl p-4 text-lg w-full outline-none focus:border-[#F97316] mb-2" />
        {results.length > 0 && (
          <div className="bg-white rounded-xl border border-[#111827]/10 mb-4 overflow-hidden">
            {results.map((c) => (
              <button key={c.id} data-testid={`community-result-${c.id}`} onClick={() => { setCommunity({ name: c.name, pin_code: c.pin_code, city: c.city, state: c.state, address_landmark: community.address_landmark }); setResults([]); }} className="w-full text-left px-4 py-3 hover:bg-[#C4B5FD]/10 border-b border-[#111827]/5 last:border-0">
                <span className="font-semibold">{c.name}</span> <span className="text-[#111827]/50 text-sm">· {c.city}, {c.pin_code}</span>
              </button>
            ))}
          </div>
        )}
        <Field label="Locality / neighbourhood name" testId="community-name" value={community.name} onChange={(e) => setCommunity({ ...community, name: e.target.value })} placeholder="Govindpur" />
        <Field label="PIN code" testId="community-pin" value={community.pin_code} onChange={(e) => setCommunity({ ...community, pin_code: e.target.value })} placeholder="831017" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="City" testId="community-city" value={community.city} onChange={(e) => setCommunity({ ...community, city: e.target.value })} placeholder="Jamshedpur" />
          <Field label="State" testId="community-state" value={community.state} onChange={(e) => setCommunity({ ...community, state: e.target.value })} placeholder="Jharkhand" />
        </div>
        <Field label="Address or landmark" testId="community-landmark" value={community.address_landmark} onChange={(e) => setCommunity({ ...community, address_landmark: e.target.value })} placeholder="Near community hall" />
        <label className="flex items-start gap-3 bg-[#C4B5FD]/15 rounded-2xl p-4 mt-2 cursor-pointer">
          <input type="checkbox" data-testid="community-confirm" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-1 w-5 h-5 accent-[#F97316]" />
          <span className="text-sm text-[#111827]/80">I confirm this is my home community. My live location, when explicitly shared during an emergency, may be different.</span>
        </label>
      </div>,
      <>{primaryBtn("Confirm community", next, "onboarding-next-community")}{back}</>
    );

  if (step === 3)
    return shell(
      <div>
        <div className="w-14 h-14 rounded-2xl bg-[#F97316]/10 flex items-center justify-center mb-4"><Users className="w-7 h-7 text-[#F97316]" /></div>
        <h1 className="text-3xl font-bold font-head tracking-tight">Trusted contacts</h1>
        <p className="text-[#111827]/60 mt-1 mb-5">Add at least two people who should be alerted in an emergency.</p>
        <div className="space-y-4">
          {contacts.map((c, i) => (
            <div key={i} className="bg-white rounded-2xl p-4 border border-[#111827]/10">
              <div className="flex justify-between items-center mb-2">
                <span className="font-bold text-sm uppercase tracking-wider text-[#111827]/60">Contact {i + 1}</span>
                {contacts.length > 1 && <button data-testid={`remove-contact-${i}`} onClick={() => setContacts(contacts.filter((_, x) => x !== i))} className="text-[#DC2626]"><Trash2 className="w-4 h-4" /></button>}
              </div>
              <input data-testid={`contact-name-${i}`} placeholder="Name" value={c.name} onChange={(e) => setContacts(contacts.map((x, xi) => xi === i ? { ...x, name: e.target.value } : x))} className="bg-[#FFF7E6] border-2 border-[#111827]/10 rounded-xl p-3 w-full mb-2 outline-none focus:border-[#F97316]" />
              <div className="grid grid-cols-2 gap-2 mb-2">
                <input data-testid={`contact-relationship-${i}`} placeholder="Relationship" value={c.relationship} onChange={(e) => setContacts(contacts.map((x, xi) => xi === i ? { ...x, relationship: e.target.value } : x))} className="bg-[#FFF7E6] border-2 border-[#111827]/10 rounded-xl p-3 outline-none focus:border-[#F97316]" />
                <input data-testid={`contact-phone-${i}`} placeholder="Phone" value={c.phone} onChange={(e) => setContacts(contacts.map((x, xi) => xi === i ? { ...x, phone: e.target.value } : x))} className="bg-[#FFF7E6] border-2 border-[#111827]/10 rounded-xl p-3 outline-none focus:border-[#F97316]" />
              </div>
              <label className="flex items-center gap-2 text-sm text-[#111827]/70"><input type="checkbox" checked={c.is_primary} onChange={(e) => setContacts(contacts.map((x, xi) => ({ ...x, is_primary: xi === i ? e.target.checked : x.is_primary })))} className="w-4 h-4 accent-[#F97316]" /> Primary contact</label>
            </div>
          ))}
        </div>
        <button data-testid="add-contact-row" onClick={() => setContacts([...contacts, { name: "", relationship: "Family", phone: "", is_primary: false }])} className="mt-4 flex items-center gap-2 text-[#F97316] font-bold"><Plus className="w-4 h-4" /> Add another contact</button>
      </div>,
      <>{primaryBtn("Continue", next, "onboarding-next-contacts")}<button onClick={() => setStep(4)} data-testid="skip-contacts" className="w-full mt-3 text-[#111827]/60 font-semibold py-2">Skip for now</button></>
    );

  if (step === 4)
    return shell(
      <div>
        <div className="w-14 h-14 rounded-2xl bg-[#F97316]/10 flex items-center justify-center mb-4"><Bell className="w-7 h-7 text-[#F97316]" /></div>
        <h1 className="text-3xl font-bold font-head tracking-tight">Emergency permissions</h1>
        <p className="text-[#111827]/60 mt-1 mb-5">We only ask for these when the related feature is used — never all at once.</p>
        <div className="space-y-3">
          {[{ i: Navigation, t: "Location", d: "Requested only when you trigger or actively share an emergency." },
            { i: Bell, t: "Notifications", d: "To alert you when a neighbour needs help." },
            { i: Camera, t: "Camera", d: "Optional, to share a photo during an incident." },
            { i: Mic, t: "Microphone", d: "Optional, for voice notes during an emergency." }].map((p, i) => (
            <div key={i} className="bg-white rounded-2xl p-4 flex items-start gap-3 border border-[#111827]/10">
              <div className="w-10 h-10 rounded-xl bg-[#C4B5FD]/25 flex items-center justify-center shrink-0"><p.i className="w-5 h-5 text-[#111827]/70" /></div>
              <div><h3 className="font-bold">{p.t}</h3><p className="text-sm text-[#111827]/60">{p.d}</p></div>
            </div>
          ))}
        </div>
        <p className="text-sm bg-[#C4B5FD]/15 rounded-2xl p-4 mt-4 text-[#111827]/80">HumNest does not continuously track you. Location is requested when you trigger or actively share an emergency.</p>
      </div>,
      <>{primaryBtn("I understand", () => setStep(5), "onboarding-next-permissions")}{back}</>
    );

  return shell(
    <div className="flex flex-col items-center text-center pt-8">
      <div className="w-20 h-20 rounded-3xl bg-[#16A34A] flex items-center justify-center shadow-lg mb-6"><PartyPopper className="w-10 h-10 text-white" /></div>
      <h1 className="text-4xl font-black font-head tracking-tight">You're ready.</h1>
      <p className="text-lg text-[#111827]/70 mt-4">HumNest is currently in active development and does not replace police, ambulance, fire services, or other official emergency services.</p>
    </div>,
    <button onClick={finish} disabled={busy} data-testid="onboarding-finish" className="w-full bg-[#F97316] text-white rounded-2xl py-4 font-bold text-lg min-h-[56px] flex items-center justify-center gap-2 disabled:opacity-70">
      {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Enter HumNest <Check className="w-5 h-5" /></>}
    </button>
  );
}
